import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import { palette } from './palette.const';

const interFont = '"Inter", "Helvetica", "Arial", sans-serif';

export const kpcTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      mode: 'dark',
      primary: {
        main: palette.primary.main,
        light: palette.primary.light,
        dark: palette.primary.dark,
      },
      secondary: {
        main: palette.secondary.main,
        light: palette.secondary.light,
        dark: palette.secondary.dark,
      },
      background: {
        default: palette.background.default,
        paper: palette.background.paper,
      },
      text: {
        primary: palette.text.primary,
        secondary: palette.text.secondary,
        disabled: palette.text.disabled,
      },
      navigation: {
        background: palette.navigation.background,
        indicator: palette.primary.main,
        color: '#ffffff',
        selectedColor: '#ffffff',
        navItem: {
          hoverBackground: 'rgba(255, 82, 1, 0.1)',
        },
      },
      status: {
        ok: palette.success,
        warning: palette.warning,
        error: palette.error,
        running: palette.info,
        pending: '#FFED51',
        aborted: '#9E9E9E',
      },
      error: {
        main: palette.error,
      },
      warning: {
        main: palette.warning,
      },
      info: {
        main: palette.info,
      },
      success: {
        main: palette.success,
      },
    },
    typography: {
      htmlFontSize: 16,
      fontFamily: interFont,
      h1: {
        fontFamily: interFont,
        fontSize: 54,
        fontWeight: 700,
        marginBottom: 10,
      },
      h2: {
        fontFamily: interFont,
        fontSize: 40,
        fontWeight: 700,
        marginBottom: 8,
      },
      h3: {
        fontFamily: interFont,
        fontSize: 32,
        fontWeight: 700,
        marginBottom: 6,
      },
      h4: {
        fontFamily: interFont,
        fontSize: 28,
        fontWeight: 700,
        marginBottom: 6,
      },
      h5: {
        fontFamily: interFont,
        fontSize: 24,
        fontWeight: 700,
        marginBottom: 4,
      },
      h6: {
        fontFamily: interFont,
        fontSize: 20,
        fontWeight: 700,
        marginBottom: 2,
      },
    },
  }),
  fontFamily: interFont,
  pageTheme: {
    home: genPageTheme({
      colors: [palette.primary.main, palette.secondary.main],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [palette.secondary.main, palette.primary.dark],
      shape: shapes.wave,
    }),
    tool: genPageTheme({
      colors: [palette.primary.dark, palette.secondary.dark],
      shape: shapes.wave,
    }),
    service: genPageTheme({
      colors: [palette.primary.main, palette.secondary.main],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [palette.secondary.main, palette.primary.main],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [palette.primary.dark, palette.secondary.main],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [palette.primary.main, palette.secondary.dark],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [palette.primary.main, palette.secondary.main],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [palette.secondary.main, palette.primary.main],
      shape: shapes.wave,
    }),
  },
  defaultPageTheme: 'home',
  components: {
    MuiGrid: {
      styleOverrides: {
        root: {
          fontFamily: interFont,
        },
        container: {
          rowGap: 16,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: interFont,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          width: 'auto',
          margin: '0px',
          boxShadow: 'none',
          borderRadius: '0px',
          borderBottom: 'none',
          backgroundImage: `${shapes.wave2}, linear-gradient(135deg, #F97316 0%, #ec9a48 45%, #f93a1e 100%)`,
        }),
        title: () => ({
          fontWeight: 600,
        }),
        subtitle: () => ({
          opacity: 0.8,
        }),
      },
    },
    BackstageSidebar: {
      styleOverrides: {
        drawer: () => ({
          backgroundColor: palette.navigation.background,
        }),
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: () => ({
          '&:hover': {
            backgroundColor: 'rgba(255, 82, 1, 0.1)',
          },
        }),
        selected: () => ({
          backgroundColor: 'rgba(255, 82, 1, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(255, 82, 1, 0.25)',
          },
          borderLeft: `3px solid ${palette.primary.main}`,
        }),
        label: () => ({
          fontWeight: 500,
        }),
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: () => ({
          backgroundColor: palette.background.paper,
          '& th': {
            backgroundColor: palette.navigation.background,
            fontWeight: 600,
          },
        }),
      },
    },
    BackstageTableHeader: {
      styleOverrides: {
        header: () => ({
          backgroundColor: palette.navigation.background,
          borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
        }),
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: () => ({
          backgroundColor: 'transparent',
        }),
        defaultTab: () => ({
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          '&:hover': {
            color: palette.primary.main,
          },
        }),
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: () => ({
          borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
        }),
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.default,
          padding: '24px',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.default,
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.paper,
          backgroundImage: 'none',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(255, 82, 1, 0.3)',
          },
        },
        containedPrimary: {
          backgroundColor: palette.primary.main,
          '&:hover': {
            backgroundColor: palette.primary.dark,
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.23)',
          '&:hover': {
            borderColor: palette.primary.main,
            backgroundColor: 'rgba(255, 82, 1, 0.08)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(255, 82, 1, 0.08)',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: palette.primary.main,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: palette.primary.main,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          fontFamily: interFont,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: interFont,
          borderBottomColor: 'rgba(255, 255, 255, 0.1)',
        },
        head: {
          fontFamily: interFont,
          fontWeight: 600,
          backgroundColor: palette.navigation.background,
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          fontFamily: interFont,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 82, 1, 0.05) !important',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
        colorPrimary: {
          backgroundColor: 'rgba(255, 82, 1, 0.2)',
          color: palette.primary.light,
        },
        colorSecondary: {
          backgroundColor: 'rgba(50, 12, 201, 0.2)',
          color: palette.secondary.light,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
        standardSuccess: {
          backgroundColor: 'rgba(102, 187, 106, 0.15)',
        },
        standardError: {
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 167, 38, 0.15)',
        },
        standardInfo: {
          backgroundColor: 'rgba(41, 182, 246, 0.15)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.paper,
          backgroundImage: 'none',
          borderRadius: '12px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.paper,
          backgroundImage: 'none',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 82, 1, 0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 82, 1, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 82, 1, 0.25)',
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: palette.secondary.light,
          '&:hover': {
            color: palette.secondary.lighter,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 82, 1, 0.1)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.4)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.primary.main,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: 'rgba(255, 255, 255, 0.5)',
        },
      },
    },
  },
});
